import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button, Input } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AUTH_FIBER_INPUT, AUTH_FIBER_LABEL, AUTH_FIBER_WIDTH } from '@/shared/ui-class';
import { AuthSplitShell } from './AuthSplitShell';

export function SignupScene() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    window.setTimeout(() => navigate('/lobby'), 900);
  };

  return (
    <Commentable name="SignupScene">
      <AuthSplitShell
        hero={
          <aside className="auth-hero hidden border-r border-[var(--hairline)] bg-[var(--surface-1)] p-12 md:flex md:flex-col md:justify-center">
            <div className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Optiflow
            </div>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
              Create an account to join the workspace. New signups wait in the lobby
              until an admin admits them.
            </p>
          </aside>
        }
      >
        <div className={`w-full ${AUTH_FIBER_WIDTH}`}>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            Sign up
          </h1>
          <p className="mt-2 text-sm text-muted">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-accent-700 dark:text-accent-400">
              Sign in
            </Link>
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <label className="block">
              <span className={AUTH_FIBER_LABEL}>Name</span>
              <Input className={AUTH_FIBER_INPUT} required />
            </label>
            <label className="block">
              <span className={AUTH_FIBER_LABEL}>Email</span>
              <Input type="email" className={AUTH_FIBER_INPUT} required />
            </label>
            <label className="block">
              <span className={AUTH_FIBER_LABEL}>Password</span>
              <Input type="password" className={AUTH_FIBER_INPUT} required />
            </label>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account'}
            </Button>
          </form>
        </div>
      </AuthSplitShell>
    </Commentable>
  );
}
